import type { TFunction } from 'i18next';
import type { FrameworkArgument } from '../framework/framework.d';
import { DRIVE_UNIT_PORT_PARAMETERS, isUnsupportedDriveUnitPortRawValue, type DriveUnitPortParameterName } from './driveUnitPortSelection';
import { isFrameworkElectronicClassArgType } from './frameworkClassNameSets';
import { formatNumericRangeBound, getSupportedNumericRangeText } from './numericRangeText';

export const isFrameworkNumberArgType = (type: string) => /^(float|double|number|int|integer)$/i.test(type || '');

export const isFrameworkIntegerArgType = (type: string) => /^(int|integer)$/i.test(type || '');

export const isFrameworkStringArgType = (type: string) => /^(string|str|text|path|path_name)$/i.test(type || '');

export const isFrameworkBoolArgType = (type: string) => /^(bool|boolean)$/i.test(type || '');

export const isPlainFrameworkNumericLiteral = (value: unknown): boolean =>
  /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/.test(String(value ?? '').trim());

/** Exact Python bool keywords the UI Select emits / accepts as canonical. */
export function isCanonicalPythonBoolLiteral(value: unknown): boolean {
  const text = String(value ?? '').trim();
  return text === 'True' || text === 'False';
}

/** Normalize JS/Python bool-ish values to Python `True` / `False` literals. */
export function toPythonBoolLiteral(value: unknown): 'True' | 'False' | null {
  if (typeof value === 'boolean') return value ? 'True' : 'False';
  const text = String(value ?? '').trim();
  if (/^True$/i.test(text)) return 'True';
  if (/^False$/i.test(text)) return 'False';
  return null;
}

/** Form/UI string for a framework arg value (bools use Python spelling). */
export function frameworkArgValueToFormString(arg: Pick<FrameworkArgument, 'type'>, value: unknown): string {
  if (value === undefined || value === null) return '';
  if (isFrameworkBoolArgType(arg.type || '')) {
    return toPythonBoolLiteral(value) ?? String(value);
  }
  return String(value);
}

/**
 * Preserve hand-authored bool source that is not exactly `True`/`False`
 * (e.g. `false`, `enabled`, `should_stop()`). Prefer parser-proven
 * `rawSourceExpression` when present.
 */
export function shouldPreserveRawFrameworkBoolArgument(arg: Pick<FrameworkArgument, 'type'>, rawValue: unknown, rawSourceValue?: unknown): boolean {
  if (!isFrameworkBoolArgType(arg.type ?? '')) return false;
  const sourceText = String(rawSourceValue ?? '').trim();
  if (sourceText) return !isCanonicalPythonBoolLiteral(sourceText);
  if (typeof rawValue === 'boolean') return false;
  const valueText = String(rawValue ?? '').trim();
  if (!valueText) return false;
  return !isCanonicalPythonBoolLiteral(valueText);
}
export const isFrameworkEnumArgType = (type: string): boolean => {
  const rawType = String(type || '').trim();
  if (!rawType) return false;
  if (isFrameworkNumberArgType(rawType) || isFrameworkStringArgType(rawType)) return false;
  if (isFrameworkElectronicClassArgType(rawType)) return false;
  return !/^(tuple|list|array|dict|boolean|bool)$/i.test(rawType);
};

const PYTHON_STRING_LITERAL_SOURCE_RE = /^(?:(?:[rRuUbBfF]|[bB][rR]|[rR][bB]|[fF][rR]|[rR][fF])("""|'''|"|')|("""|'''))/;

function isPythonExpressionLikeRawValue(rawText: string, includeOperatorExpressions = true): boolean {
  if (PYTHON_STRING_LITERAL_SOURCE_RE.test(rawText)) return true;
  if (/^lambda\b/.test(rawText)) return true;
  if (/^[A-Za-z_][\w]*(?:\.[A-Za-z_][\w]*)*\([\s\S]*\)$/.test(rawText)) return true;
  if (/^[A-Za-z_][\w]*(?:\.[A-Za-z_][\w]*)*\[[\s\S]+\]$/.test(rawText)) return true;
  return includeOperatorExpressions && hasPythonExpressionOperator(rawText);
}

function isPythonIdentifierPathRawValue(rawText: string): boolean {
  return /^[A-Za-z_][\w]*(?:\.[A-Za-z_][\w]*)*$/.test(rawText);
}

function hasPythonExpressionOperator(rawText: string): boolean {
  let quote: '"' | "'" | null = null;
  let escaped = false;
  let outsideString = '';

  for (let i = 0; i < rawText.length; i++) {
    const ch = rawText[i] ?? '';
    if (quote) {
      outsideString += ' ';
      if (escaped) {
        escaped = false;
      } else if (ch === '\\') {
        escaped = true;
      } else if (ch === quote) {
        quote = null;
      }
      continue;
    }

    if (ch === '"' || ch === "'") {
      quote = ch;
      outsideString += ' ';
      continue;
    }

    outsideString += ch;
  }

  return /\bif\b[\s\S]+\belse\b/.test(outsideString) || /\b(?:and|or|not)\b/.test(outsideString) || /(?:==|!=|<=|>=|[+\-*/%@<>])/.test(outsideString);
}

/** `motor_port`, `sensor_port`, and other 1–8 integer port kwargs on hardware cards. */
export function isDriveUnitPortParameterName(parameterName: string): parameterName is DriveUnitPortParameterName {
  return parameterName === DRIVE_UNIT_PORT_PARAMETERS.MOTOR || parameterName === DRIVE_UNIT_PORT_PARAMETERS.SENSOR;
}

export type { DriveUnitPortParameterName } from './driveUnitPortSelection';

/** Drive-unit electronic ports (1–8) used by `DriveUnitPortControl` in hardware cards and action-arg dialogs. */
export function isDriveUnitPortFrameworkArgument(arg: FrameworkArgument): boolean {
  if (isDriveUnitPortParameterName(arg.name)) return true;
  if (isFrameworkIntegerArgType(arg.type || '') && arg.min === 1 && arg.max === 8) return true;
  return false;
}

export function shouldPreserveRawDriveUnitPortArgument(arg: FrameworkArgument, rawValue: unknown): boolean {
  return isDriveUnitPortFrameworkArgument(arg) && isUnsupportedDriveUnitPortRawValue(rawValue);
}

export function shouldPreserveRawFrameworkNumericArgument(arg: FrameworkArgument, rawValue: unknown): boolean {
  const rawText = String(rawValue ?? '').trim();
  if (!rawText || !isFrameworkNumberArgType(arg.type ?? '')) return false;
  return !isPlainFrameworkNumericLiteral(rawText);
}

export function shouldPreserveRawFrameworkEnumArgument(arg: FrameworkArgument, rawValue: unknown): boolean {
  const rawText = String(rawValue ?? '').trim();
  if (!rawText || !isFrameworkEnumArgType(arg.type ?? '')) return false;
  return isPythonExpressionLikeRawValue(rawText);
}

/**
 * Preserve hand-authored electronic source that is not a plain variable / attribute path
 * (e.g. `get_sensor()`, `sensors[0]`). Prefer parser-proven `rawSourceExpression` when present.
 * Dropdown-selected sensor variable names are not preserved-as-unsupported; they serialize bare.
 */
export function shouldPreserveRawFrameworkElectronicArgument(
  arg: Pick<FrameworkArgument, 'type'>,
  rawValue: unknown,
  rawSourceValue?: unknown
): boolean {
  if (!isFrameworkElectronicClassArgType(arg.type ?? '')) return false;
  const sourceText = String(rawSourceValue ?? '').trim();
  const valueText = String(rawValue ?? '').trim();
  if (sourceText) return !isPythonIdentifierPathRawValue(sourceText);
  if (!valueText || isPythonIdentifierPathRawValue(valueText)) return false;
  return isPythonExpressionLikeRawValue(valueText);
}

export function shouldPreserveRawFrameworkStringArgument(
  arg: FrameworkArgument,
  rawValue: unknown,
  actionName?: string,
  rawSourceValue?: unknown
): boolean {
  const rawText = String(rawValue ?? '').trim();
  if (!rawText) return false;
  const isStringLikeArg = isFrameworkStringArgType(arg.type ?? '') || (actionName === 'follow_path_action' && arg.name === 'path');
  if (!isStringLikeArg) return false;
  const rawSourceText = String(rawSourceValue ?? '').trim();
  // Operator-like punctuation can be normal user text (`test-`, `%`, `&`), so only preserve
  // operator expressions when parser metadata proves the value came from source code.
  if (
    rawSourceText &&
    rawSourceText === rawText &&
    (isPythonIdentifierPathRawValue(rawSourceText) || isPythonExpressionLikeRawValue(rawSourceText))
  ) {
    return true;
  }
  return isPythonExpressionLikeRawValue(rawText, false);
}

export function coerceFrameworkArgValuePreservingExpressions(arg: FrameworkArgument, rawValue: string): string | number {
  if (shouldPreserveRawFrameworkNumericArgument(arg, rawValue)) return rawValue;
  if (shouldPreserveRawFrameworkBoolArgument(arg, rawValue)) return rawValue;
  if (shouldPreserveRawFrameworkElectronicArgument(arg, rawValue)) return rawValue;
  return coerceFrameworkArgValue(arg.type, rawValue);
}

/** Optional kwargs that may be omitted / serialized as Python `None` (schema uses `defaultValue: null`). */
export function isFrameworkArgOptionalNoneDefault(arg: FrameworkArgument): boolean {
  return arg.defaultValue === null;
}

export function coerceFrameworkArgValue(type: string, value: string): string | number {
  const t = (type || '').toLowerCase();
  if (t === 'float' || t === 'number' || t === 'double') {
    if (!isPlainFrameworkNumericLiteral(value)) return value;
    const n = parseFloat(value);
    return isNaN(n) ? value : n;
  }
  if (t === 'int' || t === 'integer') {
    if (!isPlainFrameworkNumericLiteral(value)) return value;
    const n = Number(value);
    return Number.isInteger(n) ? n : value;
  }
  if (isFrameworkBoolArgType(type)) {
    return toPythonBoolLiteral(value) ?? value;
  }
  return value;
}

export function getFrameworkNumericArgRangeText(arg: FrameworkArgument, t: TFunction, unitLabel = ''): string | null {
  if (!isFrameworkNumberArgType(arg.type ?? '')) return null;

  return getSupportedNumericRangeText(t, { min: arg.min, max: arg.max, unit: unitLabel });
}

export function getFrameworkNumericArgError(arg: FrameworkArgument, rawValue: string, t: TFunction): string | null {
  if (!isFrameworkNumberArgType(arg.type ?? '')) return null;
  if (rawValue === '') return null;
  const parsed = Number(rawValue);
  if (Number.isNaN(parsed)) return t('controller.buttonMapping.invalidNumber', 'Enter a valid number');
  if (isFrameworkIntegerArgType(arg.type ?? '') && !Number.isInteger(parsed)) {
    return t('controller.buttonMapping.integerRequired', 'Enter a whole number');
  }
  if (typeof arg.min === 'number' && parsed < arg.min) {
    if (typeof arg.max === 'number') {
      const min = formatNumericRangeBound(arg.min);
      const max = formatNumericRangeBound(arg.max);
      return t('controller.buttonMapping.rangeValueError', {
        min,
        max,
        defaultValue: `Enter a value from ${min} to ${max}`,
      });
    }
    const min = formatNumericRangeBound(arg.min);
    return t('controller.buttonMapping.minRangeValueError', { min, defaultValue: `Enter a value of at least ${min}` });
  }
  if (typeof arg.max === 'number' && parsed > arg.max) {
    if (typeof arg.min === 'number') {
      const min = formatNumericRangeBound(arg.min);
      const max = formatNumericRangeBound(arg.max);
      return t('controller.buttonMapping.rangeValueError', {
        min,
        max,
        defaultValue: `Enter a value from ${min} to ${max}`,
      });
    }
    const max = formatNumericRangeBound(arg.max);
    return t('controller.buttonMapping.maxRangeValueError', { max, defaultValue: `Enter a value no more than ${max}` });
  }
  return null;
}
